import _ from 'lodash';
import { addMultipleData, getData, getAllKeys } from './utils';
import { createPerStudyDb } from './createPerStudyDb';

// TODO(ts): `value` is the decoded beat payload built by Worker/beats.worker.ts's
// binary parsing — not derivable from a GraphQL document or schema.
export type BeatDataRecord = {
  key: number;
  value: any;
};

// Constants for Beat Info Database
const BEAT_DB_NAME = 'DB_WORKER_BEAT_INFO';
const BEAT_OBJECT_STORE_NAME = 'BEAT_WORKER';
const BEAT_OBJECT_STORE_KEY = 'key';

const beatDb = createPerStudyDb({
  dbSuffix: BEAT_DB_NAME,
  storeName: BEAT_OBJECT_STORE_NAME,
  keyPath: BEAT_OBJECT_STORE_KEY,
});

// Open databases
/**
 * Opens the per-study beat IndexedDB database and caches the handle. Builds the name from the given study id or the profile id taken from the URL, and swallows the failure with a console error when the open rejects.
 *
 * @param studyId - Study whose beat database is opened; falls back to the profile id key.
 * @returns A promise resolving once the beat database handle has been established or the failure logged.
 */
const openDBBeatData = async (studyId?: string) => {
  try {
    await beatDb.openDb(studyId);
  } catch (error) {
    console.error(`[${BEAT_DB_NAME}] Failed to open databases`, error);
  }
};

// Add multiple Beat data
/**
 * Bulk-writes beat records into the beat object store in a single readwrite transaction. Opens the beat database first when it is not yet open, then delegates to addMultipleData, which resolves with the put keys once the transaction commits or rejects on a transaction error.
 *
 * @param dataArray - Beat records to put into the store.
 * @returns A promise resolving to the array of keys produced by the put requests.
 */
const isQuotaError = (error: unknown) =>
  (error as { name?: string })?.name === 'QuotaExceededError';

const writeBeatData = (dataArray: BeatDataRecord[]) =>
  beatDb.runWrite((db) => addMultipleData(db, BEAT_OBJECT_STORE_NAME, dataArray));

const addMultipleBeatData = async (dataArray: BeatDataRecord[]) => {
  try {
    // runWrite reopens + retries once on a transient/stale-handle fault; quota is not retryable
    // there, so it falls through to the eviction path below.
    return await writeBeatData(dataArray);
  } catch (error) {
    if (!isQuotaError(error)) throw error;
    // Storage quota hit: evict the oldest closed study to free space, then retry once.
    // Dynamic import avoids a static cycle (dbMetaData imports this module).
    // eslint-disable-next-line import/no-cycle
    const { evictOldestClosedStudy } = await import('./dbMetaData');
    const freed = await evictOldestClosedStudy();
    if (!freed) throw error;
    return writeBeatData(dataArray);
  }
};

// Get data for a specific hour
/**
 * Reads the beat record stored under one hour-aligned epoch key, wrapped as an array. Opens the beat database when needed, returns a single-element array for a hit and an empty array for a miss, and logs then rethrows a wrapped error when the read fails.
 *
 * @param epoch - Hour-aligned epoch used as the record key.
 * @returns A promise resolving to the record for that hour wrapped in an array, or an empty array when none is stored.
 */
const readBeatHour = async (epoch: number): Promise<BeatDataRecord[]> => {
  if (!beatDb.db) await openDBBeatData();
  try {
    const record = await getData<BeatDataRecord>(beatDb.db!, BEAT_OBJECT_STORE_NAME, epoch);
    // concat flattens an array hit and wraps a single-record hit, matching the store shape.
    return ([] as BeatDataRecord[]).concat(record || []);
  } catch (error) {
    console.error(`[${BEAT_DB_NAME}] Error fetching data for the hour:`, error);
    throw new Error(`[${BEAT_DB_NAME}] Failed to retrieve data for the specified hour.`);
  }
};

// Get data within a range for Beat data
/**
 * Gathers beat values covering an epoch window by walking the ECG data map for overlapping segments. Keeps the segments whose moment start/stop overlap the window, returns early with an empty array when none match, otherwise reads each matching segment's hour bucket in parallel and flattens their stored values.
 *
 * @param startEpoch - Inclusive start of the requested window in epoch milliseconds.
 * @param endEpoch - Inclusive end of the requested window in epoch milliseconds.
 * @param dataEcgDataMap - ECG segment descriptors carrying moment start and stop bounds.
 * @returns A promise resolving to the accumulated beat values for the requested range.
 */
const getBeatDataForRange = async (startEpoch: number, endEpoch: number, dataEcgDataMap: any[]) => {
  // *: `data.start` and `data.stop` are moment objects -> valueOf() gives the epoch.
  // _.filter/_.map tolerate both the array (production) and object-keyed (tests) shapes.
  const usingData = _.filter(
    dataEcgDataMap,
    (data) => startEpoch <= data.stop.valueOf() && endEpoch >= data.start.valueOf(),
  );
  if (usingData.length === 0) return [];
  const chunks = await Promise.all(_.map(usingData, (data) => readBeatHour(+data.start)));
  return _.flatMap(chunks, (chunk) => chunk.map((item) => item.value));
};

// Clear Beat data from database
/**
 * Empties the beat object store without deleting the database itself. Opens the beat database when needed, clears the store, and logs that the beat data was cleared.
 *
 * @returns A promise resolving once the beat object store has been emptied.
 */
const clearDBBeatData = async () => {
  if (!beatDb.db) await openDBBeatData();
  if (beatDb.db) {
    await beatDb.clearStore();
    console.log(`[${BEAT_DB_NAME}] Cleared Beat data`);
  }
};

// Delete Beat data from database
/**
 * Drops the whole per-study beat database for one study. Composes the database name from the study id and the beat database suffix, then deletes it.
 *
 * @param studyId - Identifier of the study whose beat database is removed.
 * @returns A promise resolving once the study's beat database has been deleted.
 */
const deleteDataFromDBBeat = async (studyId: string) => {
  await beatDb.removeDb(studyId);
};

// Get count of Beat data
/**
 * Reports how many beat records are currently cached in the beat object store. Opens the beat database when needed and counts the store, yielding zero while the database is unavailable.
 *
 * @returns A promise resolving to the number of stored beat records, or zero when the database could not be opened.
 */
const getBeatDataCount = async () => {
  if (!beatDb.db) await openDBBeatData();
  if (beatDb.db) {
    const count = await beatDb.countStore();
    return count;
  }
  return 0;
};

// Get the hour-aligned keys currently stored in the beat object store
/**
 * Lists the hour-aligned epoch keys currently present in the beat object store, without loading the records. Opens the beat database when needed and yields an empty array while it is unavailable.
 *
 * @returns A promise resolving to the stored beat keys, or an empty array when the database could not be opened.
 */
const getBeatDataKeys = async (): Promise<number[]> => {
  if (!beatDb.db) await openDBBeatData();
  if (!beatDb.db) return [];
  return getAllKeys<number>(beatDb.db, BEAT_OBJECT_STORE_NAME);
};

export {
  addMultipleBeatData,
  clearDBBeatData,
  deleteDataFromDBBeat,
  getBeatDataCount,
  getBeatDataKeys,
  getBeatDataForRange,
  openDBBeatData,
};
